import { Box, Center, Flex, Text } from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import { useGame } from "../../contexts/GameContext";
import { EventTypes } from "types";
import { useTimer } from "react-timer-hook";
import { JeopardyTimerBar } from "../shared/JeopardyTimerBar";

const TIME_TO_BUZZ = 15;

// Debounce time to prevent accidental buzz-ins from category selection taps
const MOUNT_DEBOUNCE_MS = 300;

export const BuzzIn: React.FC = () => {
  const { sendEvent, currentPlayer, gameState } = useGame();
  const [buttonPressed, setButtonPressed] = useState(false);
  const hasStartedTimer = useRef(false);
  const previousQuestionPlaying = useRef<boolean | undefined>(undefined);
  const mountTimeRef = useRef<number>(Date.now());

  // Track mount time to debounce accidental taps from category selection
  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, []);

  // Disable buzz-in if question audio is playing (synced via game state)
  // This prevents buzzing in while the clue is being read on the Cast receiver
  const isQuestionPlaying = gameState?.questionAudioPlaying ?? false;
  
  // Check if player is locked out due to fair buzzing mode
  const isFairBuzzingLocked = gameState?.fairBuzzingEnabled && 
    gameState?.fairBuzzingUsedThisCycle?.[currentPlayer?.name || ''];
  
  const isDisabled = isQuestionPlaying || currentPlayer?.hasBuzzed || isFairBuzzingLocked;

  const { seconds, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
  });

  // Start timer when question audio stops playing
  useEffect(() => {
    // Detect transition from playing to not playing
    if (previousQuestionPlaying.current === true && !isQuestionPlaying && !hasStartedTimer.current) {
      hasStartedTimer.current = true;
      restart(new Date(new Date().getTime() + TIME_TO_BUZZ * 1000));
    }
    previousQuestionPlaying.current = isQuestionPlaying;
  }, [isQuestionPlaying, restart]);

  // Reset timer tracking when active clue changes
  useEffect(() => {
    hasStartedTimer.current = false;
    // If no question audio, start timer immediately when clue becomes active
    if (gameState?.activeClue && !isQuestionPlaying && !hasStartedTimer.current) {
      hasStartedTimer.current = true;
      restart(new Date(new Date().getTime() + TIME_TO_BUZZ * 1000));
    }
  }, [gameState?.activeClue?.id]);

  const onPress = async () => {
    // Ignore taps within the debounce period after mount to prevent
    // accidental buzz-ins from category selection taps
    if (Date.now() - mountTimeRef.current < MOUNT_DEBOUNCE_MS) {
      return;
    }
    if (isDisabled) return;
    setButtonPressed(true);
    await sendEvent({
      type: EventTypes.BUZZ_IN,
      playerName: currentPlayer?.name || "",
      buzzInTime: Date.now(),
    });
  };

  const onRelease = () => {
    setButtonPressed(false);
  };

  const question = gameState?.activeClue?.question || "";

  return (
    <Center height="100%" width="100%">
      {isFairBuzzingLocked ? (
        <Text textStyle="clue">Waiting for others to buzz...</Text>
      ) : currentPlayer?.hasBuzzed ? (
        <Text textStyle="clue">You've buzzed in</Text>
      ) : isQuestionPlaying ? (
        <Text textStyle="clue">Listening to clue...</Text>
      ) : (
        <Flex
          flexDir="column"
          gap="20px"
          alignItems="center"
          width="100%"
          height="100%"
          pt="80px"
          position="relative"
        >
          {/* Timer bar */}
          <Box position="absolute" top="10px" left="50%" transform="translateX(-50%)">
            <JeopardyTimerBar
              secondsRemaining={seconds}
              totalSeconds={TIME_TO_BUZZ}
              size="sm"
              position="relative"
            />
          </Box>

          {/* Tappable question text */}
          <Box
            onMouseDown={onPress}
            onMouseUp={onRelease}
            onTouchStart={onPress}
            onTouchEnd={onRelease}
            cursor="pointer"
            bg={buttonPressed ? "jeopardy.lightBlue" : "jeopardy.clueBlue"}
            borderRadius="lg"
            p={6}
            mx={4}
            width="90%"
            maxWidth="500px"
            minHeight="200px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="3px solid"
            borderColor={buttonPressed ? "jeopardy.gold" : "transparent"}
            transition="all 0.1s ease"
            _hover={{ borderColor: "jeopardy.gold" }}
            userSelect="none"
          >
            <Text
              textStyle="clue"
              fontSize={{ base: "lg", md: "xl" }}
              textAlign="center"
            >
              {question}
            </Text>
          </Box>

          <Text textStyle="clue" fontSize="sm" opacity={0.7}>
            Tap to buzz in
          </Text>
        </Flex>
      )}
    </Center>
  );
};
